/**
 * Modal for launching a Demo Studio capture from a knowledge gap.
 *
 * Gated open by GapDetail when:
 *   summary.status === 'active'
 *   && summary.resolution_type !== 'config_change'
 *   && !summary.in_flight_capture
 *
 * The trigger is surfaced inside the Resolution Options dropdown for any
 * eligible gap; users can capture against gaps classified as any
 * resolution_type other than config_change (the classifier-recommended
 * path is still flagged via a "Recommended" badge for product_capture
 * gaps).
 *
 * Flow on submit:
 *   1. POST /start-product-capture (server flips gap to resolving_via_product_capture
 *      and creates a Demo Studio session with the FK populated).
 *   2. Sequentially upload any seed files via the existing
 *      /api/demo-studio/sessions/{id}/upload-file endpoint.
 *   3. Navigate this tab to the linked Demo Studio session, matching the
 *      existing Product Studio launch flow and avoiding browser pop-up
 *      blockers entirely.
 *   4. Invalidate the gap detail + summaries queries so the in-flight
 *      indicator appears immediately on the gap detail panel.
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { FileIcon, Loader2, Upload, Video, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/use-toast';
import knowledgeGapsService, {
  type KGStartProductCaptureResponse,
  type KGSummaryResponse,
} from '@/lib/api/knowledgeGaps';
import demoStudioService from '@/lib/api/demoStudio';
import { patchKnowledgeGapSummaryCache } from '../queryCache';

// Match the existing Demo Studio launch form's 25 MB per-file cap so users
// don't hit a backend surprise after staging large attachments.
const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;

export function LaunchCaptureModal({
  open,
  onOpenChange,
  orgId,
  summary,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string;
  summary: KGSummaryResponse;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [title, setTitle] = useState<string>(`Capture for: ${summary.topic}`);
  const [initialUrl, setInitialUrl] = useState<string>('');
  const [seedFiles, setSeedFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<
    { done: number; total: number } | null
  >(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset state whenever the modal is opened so a re-open after cancel /
  // launch doesn't surface stale form values.
  useEffect(() => {
    if (open) {
      setTitle(`Capture for: ${summary.topic}`);
      setInitialUrl('');
      setSeedFiles([]);
      setDragOver(false);
      setUploadProgress(null);
    }
  }, [open, summary.topic]);

  const addFiles = (incoming: FileList | File[]) => {
    const accepted: File[] = [];
    Array.from(incoming).forEach((file) => {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast({
          title: `${file.name} is too large`,
          description: 'Maximum 25 MB per file.',
          variant: 'destructive',
        });
        return;
      }
      accepted.push(file);
    });
    if (accepted.length > 0) {
      setSeedFiles((prev) => [...prev, ...accepted]);
    }
  };

  const removeFile = (index: number) => {
    setSeedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const launch = useMutation({
    mutationFn: async () => {
      const trimmedTitle = title.trim();
      const trimmedUrl = initialUrl.trim();
      if (!trimmedTitle) {
        throw new Error('Capture name is required.');
      }
      if (!trimmedUrl) {
        throw new Error('Demo URL is required.');
      }

      const response = await knowledgeGapsService.startProductCapture(
        orgId,
        summary.id,
        { title: trimmedTitle, initial_url: trimmedUrl },
      );
      if (!response.success || !response.data) {
        const message = response.error || 'Failed to launch capture session.';
        const err = new Error(message) as Error & { code?: string };
        err.code = response.code;
        throw err;
      }
      const session: KGStartProductCaptureResponse = response.data;

      if (seedFiles.length > 0) {
        setUploadProgress({ done: 0, total: seedFiles.length });
        for (let i = 0; i < seedFiles.length; i++) {
          const file = seedFiles[i];
          try {
            const uploadResponse = await demoStudioService.uploadFileToBrowser(
              session.session_id,
              file,
            );
            if (!uploadResponse.success) {
              throw new Error(
                uploadResponse.error || `Failed to upload ${file.name}`,
              );
            }
          } catch (uploadError) {
            toast({
              title: `Couldn't attach ${file.name}`,
              description:
                uploadError instanceof Error
                  ? uploadError.message
                  : 'Upload failed — you can still continue capture.',
              variant: 'destructive',
            });
          }
          setUploadProgress({ done: i + 1, total: seedFiles.length });
        }
      }

      return session;
    },
    onSuccess: async (session) => {
      // Refresh and patch the selected gap cache before navigating away. When
      // the user returns from Studio, ActiveGapsTab initially renders from
      // cached list data; without this patch it can briefly show the stale
      // active action buttons until a manual refresh/refetch lands.
      try {
        const refreshed = await knowledgeGapsService.getSummary(orgId, summary.id);
        if (refreshed.success && refreshed.data) {
          patchKnowledgeGapSummaryCache(queryClient, orgId, refreshed.data);
        }
      } catch {
        // Best-effort cache warmup. The launch already succeeded, so keep
        // opening Studio and let the invalidations below refresh in the background.
      }

      queryClient.invalidateQueries({ queryKey: ['knowledge-gaps-summaries', orgId] });
      queryClient.invalidateQueries({
        queryKey: ['knowledge-gap-summary-detail', orgId, summary.id],
      });
      queryClient.invalidateQueries({ queryKey: ['knowledge-gaps-report', orgId] });

      const url = `/demo-studio/sessions/${session.session_id}?source=knowledge_gap&summary_id=${summary.id}`;
      toast({
        title: 'Studio launched',
        description: 'Capture session opened in Demo Studio.',
      });
      onOpenChange(false);
      navigate(url);
    },
    onError: () => {
      toast({
        title: 'Launch failed',
        description: 'We could not launch Demo Studio. Try again.',
        variant: 'destructive',
      });
    },
    onSettled: () => {
      setUploadProgress(null);
    },
  });

  const handleLaunchClick = () => {
    if (!title.trim()) {
      toast({
        title: 'Capture name is required',
        variant: 'destructive',
      });
      return;
    }
    if (!initialUrl.trim()) {
      toast({
        title: 'Demo URL is required',
        variant: 'destructive',
      });
      return;
    }

    launch.mutate();
  };

  const canLaunch =
    !launch.isPending &&
    title.trim().length > 0 &&
    initialUrl.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={(next) => !launch.isPending && onOpenChange(next)}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Video className="h-5 w-5 text-primary" /> Capture demo
          </DialogTitle>
          <DialogDescription>
            Launch a Demo Studio session linked to this knowledge gap. Once
            recorded and processed, you can apply the capture to resolve the
            gap.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="kg-launch-capture-title" className="text-xs">
              Capture name
            </Label>
            <Input
              id="kg-launch-capture-title"
              type="text"
              placeholder={`Capture for: ${summary.topic}`}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              disabled={launch.isPending}
              className="h-9"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="kg-launch-capture-url" className="text-xs">
              Demo URL
            </Label>
            <Input
              id="kg-launch-capture-url"
              type="text"
              placeholder="https://app.example.com/login"
              value={initialUrl}
              onChange={(e) => setInitialUrl(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              disabled={launch.isPending}
              className="h-9"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">
              Attachments{' '}
              <span className="font-normal text-muted-foreground">(optional)</span>
            </Label>
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                if (launch.isPending) return;
                if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                  addFiles(e.dataTransfer.files);
                }
              }}
              className={`flex items-center gap-3 rounded-md border border-dashed px-3 py-2.5 transition ${
                dragOver
                  ? 'border-primary/60 bg-primary/5'
                  : 'border-border hover:border-foreground/30'
              }`}
            >
              <Upload className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
              <span className="flex-1 text-xs text-muted-foreground">
                Drag files here or browse
              </span>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={launch.isPending}
                className="rounded-md border border-border bg-background px-2.5 py-1 text-xs font-medium text-foreground transition hover:bg-muted disabled:opacity-50"
              >
                Browse
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    addFiles(e.target.files);
                    e.target.value = '';
                  }
                }}
              />
            </div>
            {seedFiles.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {seedFiles.map((file, index) => (
                  <span
                    key={`${file.name}-${index}`}
                    className="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted/50 py-0.5 pl-2 pr-1 text-xs text-foreground"
                  >
                    <FileIcon className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
                    <span className="max-w-[180px] truncate">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      disabled={launch.isPending}
                      aria-label={`Remove ${file.name}`}
                      className="rounded p-0.5 text-muted-foreground transition hover:bg-muted hover:text-foreground disabled:opacity-50"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            {uploadProgress && (
              <p className="text-xs text-muted-foreground">
                Uploading files ({uploadProgress.done}/{uploadProgress.total})…
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={launch.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleLaunchClick}
            disabled={!canLaunch}
            className="gap-2"
          >
            {launch.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Launching…
              </>
            ) : (
              <>
                <Video className="h-4 w-4" /> Launch studio
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
